import { Cam } from "@/model/cam";
import { defineStore } from "pinia";
import { IArticle } from "@/model/IArticle";
import {
	ICountersinkParameter,
	ICylinderheadBoldHoleParameter,
	IDurchzugParameter,
	IPressJoinParameter,
	IScrewThreadParameter,
	IValidationParameter,
} from "@/model/ParameterTypes";
import { IFeature } from "@/model/IFeature";
import { IWeldlineDto } from "@/model/IWeldlineDto";
import { IValidationMessage } from "@/model/IValidationMessage";
import { EditorMode, LoadingState, MessageState, ProcessType } from "@/model/Enums";
import { IRollDirection } from "@/model/IRollDirection";
import { IMeasureResponseDto } from "@/model/IMeasureResponseDto";
import { IFace } from "@/model/IFace";

export const useMainStore = defineStore("main", {
	state: () => {
		return {
			_editorMode: EditorMode.MeasuringMode, // pseudo-private to make sure only the store can change value
			_loadingState: LoadingState.None,
			token: "", // Token taken from URL and sent via http client header
			language: "de-CH", // Language taken from URL, defaults to German if not given, sent via http client header
			loadingText: "", // Additional text to be shown below the loading spinner
			cam: new Cam(),
			article: {} as IArticle,
			features: [] as IFeature[],
			validationMessages: [] as IValidationMessage[],
			weldlines: [] as IWeldlineDto[],
			selectedFeatures: [] as IFeature[],
			screwThreadParameters: [] as IScrewThreadParameter[],
			countersinkParameters: [] as ICountersinkParameter[],
			cylinderheadBoltHoleParameters: [] as ICylinderheadBoldHoleParameter[],
			pressJoinParameter: [] as IPressJoinParameter[],
			extrudedThreadParameters: [] as IDurchzugParameter[],
			validationParameter: {} as IValidationParameter,
			rollDirection: {} as IRollDirection,
			rollDirectionLoaded: false, // true after the roll direction has been fetched once for the current model; reset when the model reloads or the user defines a new one

			skipValidation: false,
			expandedFeatures: [] as string[], // Welche Nodes im Featuretree aufgeklappt sind
			targetProcessType: ProcessType.None,
			loadingMeasurements: false,
			measureResult: null as IMeasureResponseDto | null,
			isFileSaved: true,
			errorDialog: null as { title: string; messages: string[] } | null,
			showBendzone: false,
			isTouchscreen: false,
			selectedGroupIds: [] as number[], // ids of selected elements (not just features. lines, areas, etc.)
			weldingPairs: [] as Array<IFace[]>, // selected pairs of faces to be welded
			renderGizmo: false,
			rotateObjectState: 0, // Rotate object at different speeds with states 1-3. Stop with state 0.
			zoom: 1, // current zoom factor to adapt rolldirection
			repeatBendSimulation: false, // Set to true after release cuts were created, so the bend simulation has to be repeated.
		};
	},

	getters: {
		editorMode: (state): EditorMode => state._editorMode,
		loadingState: (state): LoadingState => state._loadingState,
		isRollDirectionRelevant: (state): boolean => {
			return state.rollDirection ? state.rollDirection.isRelevant : false;
		},
		isAnyEditModeSelected: (state): boolean => {
			return state._editorMode !== undefined && state._editorMode !== null;
		},
		areAnyFeaturesSelected: (state): boolean => {
			return state.selectedFeatures && state.selectedFeatures.length > 0;
		},
		areAllSelectedFeaturesOfSameType(): boolean {
			return (
				this.areAnyFeaturesSelected &&
				this.selectedFeatures.every(
					(feature) => feature.featureType === this.selectedFeatures[0].featureType
				)
			);
		},
		useAllSelectedFeaturesSameTool(): boolean {
			return (
				this.areAnyFeaturesSelected &&
				this.selectedFeatures[0].toolId !== null &&
				this.selectedFeatures[0].toolId.length > 0 &&
				this.selectedFeatures.every(
					(feature) => feature.toolId === this.selectedFeatures[0].toolId
				)
			);
		},
		hasOpenValidationMessages(): boolean {
			return this.validationMessages
				? this.validationMessages.some(
						(m: IValidationMessage) => m.state === MessageState.Open
					)
				: false;
		},
		// Checks if any areas are selected to be welded.
		hasWeldingPairs(): boolean {
			return this.weldingPairs.length > 0;
		},
		// Checks if the latest welding pair is complete.
		// If so, the next selected area creates a new welding pair.
		hasPairedWeldingFaces(): boolean {
			if (!this.hasWeldingPairs) return false;
			return this.weldingPairs[this.weldingPairs.length - 1].length % 2 === 0;
		},
		currentWeldingPair(): IFace[] {
			if (!this.hasWeldingPairs) return [];
			return this.weldingPairs[this.weldingPairs.length - 1];
		},
		selectedWeldlines(): IWeldlineDto[] {
			if (!this.weldlines) return [];
			return this.weldlines.filter((w: IWeldlineDto) => w.selected);
		},
	},

	actions: {
		setEditorMode(mode: EditorMode) {
			if (mode != this._editorMode) {
				// Deselect all selected elements and features when mode is changed
				// can't assign new empty arrays because that breaks the connection to cam.
				this.cam.deselectAll();
				this.setAllWeldlinesVisibility(mode === EditorMode.WeldMode);
				this._editorMode = mode;
			}
		},
		setLoadingState(state: LoadingState) {
			this._loadingState = state;
			// Reset the loading text if the new state is non-"loading" to prevent leftover text
			if (
				this._loadingState === LoadingState.Done ||
				this._loadingState === LoadingState.None
			) {
				this.loadingText = "";
			}
		},
		addOrRemoveSelectedFeature(featureId: number, checked: boolean) {
			const selectedFeatureIndex = this.selectedFeatures.findIndex(
				(feature: IFeature) => feature.featureId === featureId
			);
			if (checked) {
				if (selectedFeatureIndex < 0) {
					// Add if checkbox was checked and it's not already in the store
					const featureToAdd = this.features.find(
						(feature: IFeature) => feature.featureId === featureId
					);
					this.selectedFeatures.push(featureToAdd!);
				}
			} else {
				if (selectedFeatureIndex >= 0) {
					// Remove if checkbox was unchecked and it's currently in the store
					this.selectedFeatures.splice(selectedFeatureIndex, 1);
				}
			}
		},
		addOrRemoveSelectedFeatures(featureIds: number[], checked: boolean) {
			const currentSelectedFeatureids = this.selectedFeatures.map((f) => f.featureId);
			if (checked) {
				var toAddFeatures = this.features.filter(
					(f) =>
						featureIds.includes(f.featureId) &&
						!currentSelectedFeatureids.includes(f.featureId)
				);
				this.selectedFeatures.push(...toAddFeatures);
			} else {
				this.selectedFeatures.splice(
					0,
					this.selectedFeatures.length,
					...this.selectedFeatures.filter((f) => !featureIds.includes(f.featureId))
				);
			}
		},
		addOrRemoveExpandedFeature(key: string) {
			const index = this.expandedFeatures.indexOf(key);
			if (index !== -1) {
				// Key exists, remove it
				this.expandedFeatures.splice(index, 1);
			} else {
				// Key doesn't exist, add it
				this.expandedFeatures.push(key);
			}
		},
		replaceFeatures(features: IFeature[]) {
			this.features.splice(0, this.features.length);
			this.features.push(...features);
		},
		emptySelectedFeatures() {
			this.selectedFeatures.splice(0, this.selectedFeatures.length);
		},
		emptySelectedGroupIds() {
			this.selectedGroupIds.splice(0, this.selectedGroupIds.length);
		},
		emptyCurrentWeldingPair() {
			// This only removes the faces in the current pair if it's not "full" yet
			if (!this.hasWeldingPairs || this.hasPairedWeldingFaces) return;
			this.weldingPairs.splice(this.weldingPairs.length - 1, 1);
		},
		emptyAllWeldingPairs() {
			if (!this.hasWeldingPairs) return;
			this.weldingPairs.splice(0, this.weldingPairs.length);
		},
		replaceValidationMessages(messages: IValidationMessage[]) {
			this.validationMessages.splice(0, this.validationMessages.length);
			this.validationMessages.push(...messages);
		},
		setAllWeldlinesVisibility(show: boolean) {
			if (!this.weldlines) return;
			return this.weldlines.forEach((w: IWeldlineDto) => (w.selected = show));
		},
	},
});
